import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { H3 } from '../../../AbstractElements';
import { Attivita, CategoryApi, AttivitaApi, AttivitaFilters, Utenti, TaskApi, CommentiApi } from '../../../Types/Panel.type';
import { parseMetaName } from '../../../Helpers';
import Loader from '../../../Components/Widgets/Loader/generic';
import { get, postJson, putJson } from '../../../hook/request';
import UserAutocomplete, { UtenteSearch } from '../../../Components/Autocomplete/UserAutocomplete';
import DescrizioneEditor from './DescrizioneEditor';
import FiltriAttivita from './FiltriAttivita';
import DeleteConfirm from '../../../Components/Modals/Delete/DopdownDelete';
import AttivitaTaskDrawer from './AttivitaTaskDrawer';
import CommentiDrawer from './CommentiDrawer';
import {formatDateIT} from '../../../Helpers/index'

type CategoryOption = { id: number; label: string };

export default function AttivitaTable() {
    const { id: progettoId, attivita: attivitaId } = useParams<{ id: string; attivita?: string }>();
    const [rows, setRows] = useState<Attivita[]>([]);
    const [rowToDelete, setRowToDelete] = useState<Attivita | null>(null);
    const [loading, setLoading] = useState(false);
    const [stato, setStato] = useState<CategoryOption[]>([]);
    const [tipo, setTipo] = useState<CategoryOption[]>([]);
    const [activeAttivita, setActiveAttivita] = useState<Attivita | null>(null);
    const [closedDetail, setClosedDetail] = useState<string | null>(null);
    const [taskDrawerAttivita, setTaskDrawerAttivita] = useState<Attivita | null>(null);
    const [commentiDrawerAttivita, setCommentiDrawerAttivita] = useState<Attivita | null>(null);
    const navigate = useNavigate();

    // Filtri
    const [filters, setFilters] = useState<AttivitaFilters>({
        attivitaID: attivitaId ?? '',
        ticket: false,
        ticketID: '',
        stato: '',
        tipo: '',
        data_svolgere: '',
        data_esecuzione: '',
        utenti:[]
    });

    // Il secondo parametro dell'URL identifica l'attività da cercare.
    useEffect(() => {
        setFilters(current => {
            const nextAttivitaID = attivitaId ?? '';
            return current.attivitaID === nextAttivitaID
                ? current
                : { ...current, attivitaID: nextAttivitaID };
        });
    }, [attivitaId]);

    // se c'è un'attività in URL la seleziono
    useEffect(() => {
        if (closedDetail === `${progettoId}/${attivitaId}`) {
            setActiveAttivita(null);
            return;
        }
        if (attivitaId && rows.length > 0) {
            const found = rows.find(r => String(r.ID) === attivitaId);
            setActiveAttivita(found ?? null);
        } else {
            setActiveAttivita(null);
        }
    }, [attivitaId, progettoId, rows, closedDetail]);

    function handleFiltersChange(next: AttivitaFilters) {
        setFilters(next);
    }

    // ================== LOAD ATTIVITÀ ==================
    async function loadAttivita(currentFilters: AttivitaFilters = filters) {
        if (!progettoId) return;
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.set('ID', String(progettoId));
            if (currentFilters.attivitaID) params.set('attivitaID', currentFilters.attivitaID);
            if (currentFilters.ticket) params.set('ticket', '1');
            if (currentFilters.ticketID.trim()) params.set('ticketID', currentFilters.ticketID.trim());
            if (currentFilters.stato) params.set('stato', currentFilters.stato);
            if (currentFilters.tipo) params.set('tipo', currentFilters.tipo);
            if (currentFilters.data_svolgere) params.set('data_svolgere', currentFilters.data_svolgere);
            if (currentFilters.data_esecuzione) params.set('data_esecuzione', currentFilters.data_esecuzione);
            if (currentFilters.utenti && currentFilters.utenti.length > 0) {
                currentFilters.utenti.forEach(u => {
                    if (u.ID) {
                    params.append('utenti[]', u.ID);
                    }
                });
            }

            const url = `/progetti/attivita?${params.toString()}`;

            const res = await get<AttivitaApi[] | { data?: AttivitaApi[] }>(url);
            const payload = (res as any)?.data ?? res;
            const rawList: AttivitaApi[] = Array.isArray(payload) ? payload : [];

            const list: Attivita[] = rawList.map(a => {
                const uniqueUsers: Utenti[] = [];
                (a.utenti || []).forEach(u => {
                    const idStr = String(u.ID);
                    if (!uniqueUsers.some(x => x.ID === idStr)) {
                        uniqueUsers.push({
                            ID: idStr,
                            nominativo: u.nominativo || `${u.nome ?? ''} ${u.cognome ?? ''}`.trim(),
                            email: u.email,
                        });
                    }
                });

                const firstTask: TaskApi | undefined = a.task && a.task.length > 0 ? a.task[0] : undefined;
                const commenti: CommentiApi | undefined = a.commenti && a.commenti.length > 0 ? a.commenti[0] : undefined;
                return {
                    ID: a.ID,
                    ticketID: a.ticketID,
                    descrizione: a.descrizione,
                    priorita: a.priorita,
                    stato: String(a.stato ?? ''),
                    tipo: String(a.tipo ?? ''),
                    rapportino: a.rapportino === 1,
                    tempo_esecuzione: a.tempo_esecuzione ? a.tempo_esecuzione.slice(0, 5) : '',
                    data_svolgere: a.data_svolgere,
                    data_esecuzione: a.data_esecuzione,
                    data_ins: a.data_ins,
                    utenti: uniqueUsers,
                    task: firstTask
                        ? {
                            task_svolto: firstTask.task_svolto ?? 0,
                            task_totali: firstTask.task_totali ?? 0,
                        }
                        : {
                            task_svolto: 0,
                            task_totali: 0,
                        },
                    commenti: commenti ? { comm_totali: commenti.comm_totali } : { comm_totali: 0 },
                    _isNew: false,
                    _isSaving: false,
                    _clientKey: `row-${a.ID}`,
                };
            });



            setRows(list);
        } finally {
            setLoading(false);
        }
    }

    // ================== LOAD CATEGORIE (STATO / TIPO) ==================
    useEffect(() => {
        (async () => {
            try {
                const catRes = await get<CategoryApi[] | { data?: CategoryApi[] }>(`/categories/parent?ID=669`);
                const catRoot: any = (catRes as any)?.data ?? catRes;
                const catArr: CategoryApi[] = Array.isArray(catRoot)
                    ? catRoot
                    : Array.isArray(catRoot?.data)
                        ? catRoot.data
                        : [];
                const catOpts: CategoryOption[] = catArr
                    .filter(c => (c.abilitato ?? 1) === 1)
                    .sort((a, b) => (a.ordine ?? 0) - (b.ordine ?? 0))
                    .map(c => ({
                        id: Number(c.ID),
                        label: parseMetaName(c.meta, 'it') || c.codice || String(c.ID),
                    }));
                setStato(catOpts);
                const catType = await get<CategoryApi[] | { data?: CategoryApi[] }>(`/categories/parent?ID=672`);
                const typeRoot: any = (catType as any)?.data ?? catType;
                const typeArr: CategoryApi[] = Array.isArray(typeRoot)
                    ? typeRoot
                    : Array.isArray(typeRoot?.data)
                        ? typeRoot.data
                        : [];
                const typeOpts: CategoryOption[] = typeArr
                    .filter(c => (c.abilitato ?? 1) === 1)
                    .sort((a, b) => (a.ordine ?? 0) - (b.ordine ?? 0))
                    .map(c => ({
                        id: Number(c.ID),
                        label: parseMetaName(c.meta, 'it') || c.codice || String(c.ID),
                    }));
                setTipo(typeOpts);
            } catch (err) {
                console.error('Errore caricando le categorie:', err);
            }
        })();
    }, []);

    useEffect(() => {
        loadAttivita();
    }, [progettoId, filters]);

    function handleChangeRow(index: number, field: keyof Attivita, value: any) {
        setRows(prev => {
            const copy = [...prev];
            copy[index] = { ...copy[index], [field]: value };
            return copy;
        });
    }

    function handleAddRow() {
        setRows(prev => [
            {
                descrizione: '',
                stato: '',
                tipo: '',
                utenti: [],
                rapportino: false,
                tempo_esecuzione: '',
                priorita: 0,
                data_svolgere: '',
                data_esecuzione: '',
                _isNew: true,
                _isSaving: false,
                _clientKey: `new-${Date.now()}-${Math.random()}`,
            },
            ...prev,
        ]);
    }

    async function handleSaveRow(
        index: number,
        rowOverride?: Attivita,
        opts?: { reload?: boolean }
    ): Promise<number | undefined> {
        if (!progettoId) return;

        const row = rowOverride ?? rows[index];
        if (!row) return;

        setRows(prev => {
            const copy = [...prev];
            if (!copy[index]) return prev;
            copy[index] = { ...copy[index], _isSaving: true };
            return copy;
        });

        let savedId: number | undefined = row.ID;

        try {
            const isNew = !row.ID;
            const { _isNew, _isSaving, ...rest } = row;

            const payload = {
                progetto_id: Number(progettoId),
                ...rest,
                rapportino: row.rapportino ? 1 : 0,
                tempo_esecuzione: row.tempo_esecuzione ? `${row.tempo_esecuzione}:00` : null,
                utenti: row.utenti.map(u => ({
                    ID: Number(u.ID),
                    nominativo: u.nominativo,
                    email: u.email,
                })),
            };

            if (isNew) {
                const res = await postJson('/progetti/nuova_attivita', payload);
                const body: any = (res as any)?.data ?? res;
                const newId = Number(body.attivita ?? body.ID ?? body.id);
                savedId = newId || row.ID;
                toast.success('Attività aggiunta con successo');
                setRows(prev => {
                    const copy = [...prev];
                    if (!copy[index]) return prev;
                    copy[index] = {
                        ...copy[index],
                        ID: savedId,
                        _isNew: false,
                    };
                    return copy;
                });
            } else {
                await putJson(`/progetti/attivita?ID=${row.ID}`, payload);
                savedId = row.ID;
                toast.success('Attività aggiornata con successo');
            }

            if (opts?.reload !== false) {
                await loadAttivita();
            }
        } catch (err) {
            console.error('Errore salvataggio attività', err);
        } finally {
            setRows(prev => {
                const copy = [...prev];
                if (!copy[index]) return copy;
                copy[index] = { ...copy[index], _isSaving: false };
                return copy;
            });
        }

        return savedId;
    }

    function handleRemoveNewRow(index: number) {
        setRows(prev => prev.filter((_, i) => i !== index));
    }

    // ================== UTENTI PER RIGA ==================
    function handleAddUserToRow(rowIndex: number, user: UtenteSearch) {
        setRows(prev => {
            const copy = [...prev];
            const current = copy[rowIndex];
            if (!current) return prev;

            const already = current.utenti?.some(u => u.ID === String(user.ID));
            const nuoviUtenti = already
                ? current.utenti
                : [
                    ...(current.utenti || []),
                    {
                        ID: String(user.ID),
                        nominativo: `${user.nome} ${user.cognome}`,
                        email: user.email ?? '',
                    },
                ];

            copy[rowIndex] = { ...current, utenti: nuoviUtenti };
            return copy;
        });
    }

    function handleRemoveUserFromRow(rowIndex: number, userId: string) {
        setRows(prev => {
            const copy = [...prev];
            const current = copy[rowIndex];
            if (!current) return prev;

            copy[rowIndex] = {
                ...current,
                utenti: (current.utenti || []).filter(u => u.ID !== userId),
            };
            return copy;
        });
    }

    return (
        <div className="page-body pt-4 w-100">
            <div>
                <div />
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <H3 className="mb-0">Attività</H3>
                </div>
                <div>
                    <FiltriAttivita
                        filters={filters}
                        onChange={handleFiltersChange}
                        statoOptions={stato}
                        tipoOptions={tipo}
                        disabled={loading}
                        onAddRow={handleAddRow}
                    />
                </div>
            </div>

            {loading ? (
                <Loader />
            ) : (
                <div className="table-activity">
                    <div style={{ paddingBottom: 250 }} className="attivita-table-wrapper">
                        <table className="table table-sm table-bordered align-middle attivita-table " style={{ tableLayout: 'fixed' }}>
                            <thead className="table-light">
                                <tr>
                                    <th style={{ width: 100 }}>Azioni</th>
                                    <th style={{ width: 600 }}>Descrizione</th>
                                    <th style={{ width: 100 }}>Stato</th>
                                    <th style={{ width: 100 }}>Tipo</th>
                                    <th style={{ width: 100 }}>Priorità</th>
                                    <th style={{ width: 100 }}>Utenti</th>
                                    <th style={{ width: 100 }}>Tempo esecuzione</th>
                                    <th style={{ width: 100 }}>Data da svolgere</th>
                                    <th style={{ width: 100 }}>Data esecuzione</th>
                                    <th style={{ width: 100 }}>Rapportino</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length === 0 && (
                                    <tr>
                                        <td colSpan={9} className="text-center">
                                            Nessuna attività
                                        </td>
                                    </tr>
                                )}

                                {rows.map((row, index) => (
                                    <tr key={row._clientKey ?? row.ID ?? `row-${index}`}>
                                        <td width={60}>
                                            <div className="d-flex gap-2 justify-content-center">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-primary"
                                                    onClick={() => handleSaveRow(index)}
                                                    disabled={row._isSaving}
                                                >
                                                    {row._isSaving ? 'Salvo...' : <i className="fa fa-floppy-o" title='Salva' aria-hidden="true"></i>}
                                                </button>

                                                {!row._isNew && (
                                                    <UncontrolledDropdown>
                                                        <DropdownToggle
                                                            style={{ borderRadius: 4 }}
                                                            caret
                                                            size="sm"
                                                            color="light"
                                                            className="border"
                                                        >
                                                            <i className="fa-solid fa-ellipsis" title='azioni'></i>
                                                        </DropdownToggle>

                                                        <DropdownMenu end>
                                                            <DropdownItem
                                                                onClick={() => {
                                                                    setClosedDetail(null);
                                                                    setActiveAttivita(row);
                                                                    navigate(`${process.env.PUBLIC_URL || ''}/progetti/${progettoId}/${row.ID}`);
                                                                }}
                                                            >
                                                                <i className="fa-solid fa-info me-2" />
                                                                Dettaglio
                                                            </DropdownItem>

                                                            <DropdownItem onClick={() => setTaskDrawerAttivita(row)}>
                                                                <i className="fa-solid fa-list-check me-2" />
                                                                Task
                                                            </DropdownItem>

                                                            <DropdownItem onClick={() => setCommentiDrawerAttivita(row)}>
                                                                <i className="fa-regular fa-comment me-1" />
                                                                Commenti
                                                            </DropdownItem>

                                                            <DropdownItem divider />


                                                            <DropdownItem
                                                                className="text-danger"
                                                                onClick={() => setRowToDelete(row)}
                                                            >
                                                                <i className="fa-solid fa-trash me-2" />
                                                                Elimina
                                                            </DropdownItem>
                                                        </DropdownMenu>
                                                    </UncontrolledDropdown>
                                                )}

                                                {row._isNew && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => handleRemoveNewRow(index)}
                                                    >
                                                        Annulla
                                                    </button>
                                                )}
                                            </div>
                                        </td>

                                        <td>
                                            <div className="d-flex align-items-center gap-1">
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    value={row.descrizione ?? ''}
                                                    onChange={e =>
                                                        handleChangeRow(index, 'descrizione', e.target.value)
                                                    }
                                                />

                                                <DescrizioneEditor
                                                    value={row}
                                                    statoOptions={stato}
                                                    tipoOptions={tipo}
                                                    onSave={async updatedRow => {
                                                        setRows(prev => {
                                                            const copy = [...prev];
                                                            copy[index] = { ...copy[index], ...updatedRow };
                                                            return copy;
                                                        });

                                                        const savedId = await handleSaveRow(index, updatedRow, {
                                                            reload: false,
                                                        });

                                                        const finalRow: Attivita = {
                                                            ...updatedRow,
                                                            ID: savedId ?? updatedRow.ID,
                                                            _isNew: false,
                                                        };

                                                        setRows(prev => {
                                                            const copy = [...prev];
                                                            const current = copy[index];
                                                            if (!current) return prev;
                                                            copy[index] = { ...current, ...finalRow };
                                                            return copy;
                                                        });

                                                        return finalRow;
                                                    }}
                                                    onOpenTask={att => setTaskDrawerAttivita(att)}

                                                    isOpen={row.ID ? activeAttivita !== null && activeAttivita.ID === row.ID : undefined}
                                                    onOpen={() => {
                                                        setClosedDetail(null);
                                                        setActiveAttivita(row);
                                                    }}
                                                    onClose={() => {
                                                    setClosedDetail(`${progettoId}/${attivitaId}`);
                                                    setActiveAttivita(null);
                                                    }}
                                                />
                                            </div>
                                           <div className='d-flex justify-content-between mt-1'>
                                             <div className='d-flex flex-row mt-2 align-items-center gap-4'>
                                                {(row.task?.task_totali && row.task?.task_totali > 0) ? <div onClick={() => setTaskDrawerAttivita(row)} style={{ cursor: 'pointer' }}>
                                                    <i className="fa-solid fa-pen-to-square"></i> <b style={{ color: 'green' }}>{row.task.task_svolto}</b> su <b style={{ color: 'red' }}>{row.task.task_totali}</b>
                                                </div>
                                                    : ''}

                                                {row.commenti?.comm_totali && row.commenti?.comm_totali > 0 ?
                                                    <div className='pointer' onClick={() => setCommentiDrawerAttivita(row)}>
                                                        <i className="fa-regular fa-comment"></i> {row.commenti.comm_totali}
                                                    </div>
                                                    : ''
                                                }
                                            </div>
                                            <div>
                                                {row.ticketID && <Link className="text-primary fw-bold me-2" to={`${process.env.PUBLIC_URL || ''}/gestione-email/${encodeURIComponent(row.ticketID)}`} onClick={event => event.stopPropagation()}>{row.ticketID}</Link>}
                                                {row.data_ins && 
                                                    <span style={{color:'grey', fontSize:'12px'}}>{formatDateIT(row.data_ins, false)}</span>
                                                }
                                            </div>
                                           </div>
                                        </td>

                                        {/* Stato */}
                                        <td>
                                            <select
                                                className="form-select form-select-sm"
                                                value={row.stato ?? ''}
                                                onChange={e => handleChangeRow(index, 'stato', e.target.value)}
                                            >
                                                <option value="">-- Seleziona stato --</option>
                                                {stato.map(opt => (
                                                    <option key={opt.id} value={String(opt.id)}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>

                                        {/* Tipo */}
                                        <td>
                                            <select
                                                className="form-select form-select-sm"
                                                value={row.tipo ?? ''}
                                                onChange={e => handleChangeRow(index, 'tipo', e.target.value)}
                                            >
                                                <option value="">-- Seleziona tipo --</option>
                                                {tipo.map(opt => (
                                                    <option key={opt.id} value={String(opt.id)}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>

                                        <td>
                                            <select
                                                className="form-select form-select-sm"
                                                value={row.priorita ?? 1}
                                                onChange={e =>
                                                    handleChangeRow(index, 'priorita', Number(e.target.value))
                                                }
                                            >
                                                <option value={3}>🔴 Alto</option>
                                                <option value={2}>🟡 Medio</option>
                                                <option value={1}>🟢 Basso</option>
                                            </select>
                                        </td>

                                        {/* Utenti */}
                                        <td>
                                            <UserAutocomplete
                                                selectedUsers={row.utenti || []}
                                                onAddUser={u => handleAddUserToRow(index, u)}
                                                onRemoveUser={id => handleRemoveUserFromRow(index, id)}
                                            />
                                        </td>

                                        {/* Tempo esecuzione */}
                                        <td>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                placeholder="HH:MM"
                                                value={row.tempo_esecuzione ?? ''}
                                                onChange={e => {
                                                    const value = e.target.value;
                                                    if (/^\d{0,5}(:[0-5]?\d?)?$/.test(value)) {
                                                        handleChangeRow(index, 'tempo_esecuzione', value);
                                                    }
                                                }}
                                            />
                                        </td>

                                        {/* Data da svolgere */}
                                        <td>
                                            <input
                                                type="date"
                                                className="form-control form-control-sm"
                                                value={row.data_svolgere?.slice(0, 10) ?? ''}
                                                onChange={e =>
                                                    handleChangeRow(index, 'data_svolgere', e.target.value)
                                                }
                                            />
                                        </td>

                                        {/* Data esecuzione */}
                                        <td>
                                            <input
                                                type="date"
                                                className="form-control form-control-sm"
                                                value={row.data_esecuzione?.slice(0, 10) ?? ''}
                                                onChange={e =>
                                                    handleChangeRow(index, 'data_esecuzione', e.target.value)
                                                }
                                            />
                                        </td>

                                        <td className="text-center">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={!!row.rapportino}
                                                onChange={e =>
                                                    handleChangeRow(index, 'rapportino', e.target.checked)
                                                }
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}


            <AttivitaTaskDrawer
                open={!!taskDrawerAttivita}
                attivita={taskDrawerAttivita}
                onClose={() => setTaskDrawerAttivita(null)}
            />

            <CommentiDrawer
                open={!!commentiDrawerAttivita}
                attivita={commentiDrawerAttivita}
                onClose={() => setCommentiDrawerAttivita(null)}
            />

            <DeleteConfirm
                url={
                    rowToDelete
                        ? `/progetti/attivita?ID=${rowToDelete.ID}`
                        : ''
                }
                isOpen={!!rowToDelete}
                onToggle={open => {
                    if (!open) setRowToDelete(null);
                }}
                onSuccess={async () => {
                    setRowToDelete(null);
                    await loadAttivita();
                }}
                onError={() => {
                    setRowToDelete(null);
                }}
            />
        </div>
    );
}
